import { EventAudienceType } from '@/src/constants/shared';
import { EditEventActions, useEditEvent } from '@/src/store/edit-event.context';
import { useUser } from '@/src/store/user.context';
import { Event } from '@/src/types/Event';
import { EventPrivacyType } from '@/src/types/EventPrivacyType';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Alert, Container, FormField, Grid, Header, SpaceBetween, Textarea, Tiles } from '@amzn/awsui-components-react';
import { BaseChangeDetail } from '@amzn/awsui-components-react/polaris/input/interfaces';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PrivacyTypeProps {
  target: Event | undefined;
}

interface PublicEventDetailsInputProps {
  target: Event | undefined;
}

const MAX_PUBLIC_DESCRIPTION_LENGTH = 1000;
const MIN_PUBLIC_DESCRIPTION_LENGTH = 40;
const PublicEventDetailsInput: React.FC<PublicEventDetailsInputProps> = ({ target }) => {
  const { t } = useTranslation();
  const { handleUpdateEditEvent } = useEditEvent();
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  const handleTextAreaChange = useCallback(
    ({ detail }) => {
      const description = (detail as BaseChangeDetail).value;
      // don't allow more than a 1000 chars
      if (description.length > MAX_PUBLIC_DESCRIPTION_LENGTH) {
        setError(
          t(i18nKeys.events.eventDetails.labels.publicEventSettings.maxDescriptionError, {
            MAX_PUBLIC_DESCRIPTION_LENGTH,
          })
        );
        setShowError(true);
        return;
      }
      if (description.length < MIN_PUBLIC_DESCRIPTION_LENGTH) {
        setError(
          t(i18nKeys.events.eventDetails.labels.publicEventSettings.minDescriptionError, {
            MIN_PUBLIC_DESCRIPTION_LENGTH,
          })
        );
      } else {
        setError('');
        setShowError(false);
      }

      handleUpdateEditEvent(EditEventActions.PUBLIC_DESCRIPTION, detail.value);
    },
    [t]
  );

  const handleTextAreaBlur = useCallback(() => {
    if (error) {
      setShowError(true);
      return;
    }
    setShowError(false);
  }, [error]);

  return (
    <FormField
      label={<b>{t(i18nKeys.events.eventDetails.labels.publicEventSettings.description)}</b>}
      description={t(i18nKeys.events.eventDetails.labels.publicEventSettings.descriptionHelpText)}
      i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}
      errorText={showError ? error : ''}
    >
      <Textarea
        value={target?.publicEventDetails.description ?? ''}
        onChange={handleTextAreaChange}
        onBlur={handleTextAreaBlur}
        placeholder={t(i18nKeys.events.eventDetails.labels.publicEventSettings.descriptionPlaceholder)}
      />
    </FormField>
  );
};

const PrivacyType: React.FC<PrivacyTypeProps> = ({ target }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { handleUpdateEditEvent, newEventMode, editMode } = useEditEvent();

  // privacy can be edited in new event mode or by Jam-Admin role when in edit mode
  const canEdit = newEventMode || ((user?.isSuperAdmin ?? false) && editMode);

  // if there is no set event privacy type then set to private code as a default choice
  useEffect(() => {
    if (user && target?.eventPrivacyType === null) {
      handleUpdateEditEvent(EditEventActions.EVENT_PRIVACY_TYPE, EventPrivacyType.PRIVATE_INVITE);
    }
  }, [user, target, handleUpdateEditEvent]);

  const renderContent = () => {
    const AdminEventPrivacyTypes = Object.keys(EventPrivacyType).map((type) => ({
      label: t(i18nKeys.events.eventDetails.labels.evenPrivacyType[type as keyof typeof EventPrivacyType]),
      value: EventPrivacyType[type as keyof typeof EventPrivacyType],
      description: t(i18nKeys.events.eventDetails.labels.evenPrivacyTypeDesc[type as keyof typeof EventPrivacyType]),
    }));

    const EventPrivacyTypes = AdminEventPrivacyTypes.filter((f) => {
      if (target?.audienceType === EventAudienceType.SUMMIT) {
        return f.value !== EventPrivacyType.PUBLIC && f.value !== EventPrivacyType.PRIVATE_CODE;
      }
      return f.value !== EventPrivacyType.PUBLIC;
    });
    const SbtsLearnerPrivacyTypes = AdminEventPrivacyTypes.filter((f) => f.value === EventPrivacyType.PRIVATE_INVITE);

    if (user) {
      const items = user.isSuperAdmin
        ? AdminEventPrivacyTypes
        : user.isSubscribedUser || !user.isSbtsLearner
        ? EventPrivacyTypes
        : SbtsLearnerPrivacyTypes;

      if (canEdit) {
        return (
          <>
            <Tiles
              onChange={({ detail }) => {
                handleUpdateEditEvent(EditEventActions.EVENT_PRIVACY_TYPE, detail.value);
              }}
              value={target?.eventPrivacyType || ''}
              // SuperAdmin users see all privacy types
              // Users with subscribed-user role (or without sbts-learner role) see both PRIVATE_INVITE and PRIVATE_CODE
              // Users with only sbts-learner role see only PRIVATE_INVITE
              items={items}
              data-testid="eventPrivacyTypeTiles"
            />
          </>
        );
      } else {
        // When not in edit mode, just show the selected tile without interaction
        const selectedItem = items.find((item) => item.value === target?.eventPrivacyType);
        return (
          <div className="awsui-tiles-container">
            <div className="awsui-tiles-item awsui-tiles-item-selected">
              <div className="awsui-tiles-item-content">
                <div className="awsui-tiles-item-title">{selectedItem?.label || target?.eventPrivacyType}</div>
                {selectedItem?.description && (
                  <div className="awsui-tiles-item-description">{selectedItem.description}</div>
                )}
              </div>
            </div>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <Container header={<Header variant="h2">{t(i18nKeys.events.eventDetails.headers.eventPrivacyType)}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <Grid gridDefinition={[{ colspan: 8 }]}>{renderContent()}</Grid>
        {user && canEdit && target?.eventPrivacyType === EventPrivacyType.PRIVATE_CODE && (
          <Alert type="warning">
            {t(i18nKeys.events.eventDetails.labels.evenPrivacyType.privateCodeApprovalWarning)}
          </Alert>
        )}
        {user && canEdit && target?.eventPrivacyType === EventPrivacyType.PUBLIC && (
          <div>
            <Alert type="warning">{t(i18nKeys.events.eventDetails.labels.evenPrivacyType.publicApprovalWarning)}</Alert>
            <PublicEventDetailsInput target={target} />
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default PrivacyType;
